import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import ItemCard from '../components/ItemCard.jsx'
import WeaponArt from '../components/WeaponArt.jsx'
import CoinIcon from '../components/CoinIcon.jsx'
import {
  formatRub,
  plural,
  RARITY_COLORS,
  RARITY_LABELS,
} from '../lib/format.js'
import '../styles/picker.css'
import '../styles/crafts.css'

/** Сколько предметов съедает контракт. Столько же требует и сервер. */
const SLOT_COUNT = 10

/**
 * Контракты: 10 предметов одной редкости → 1 предмет.
 *
 * Раскладка — как в апгрейде: сверху сцена (слоты → стрелка → выход), под ней
 * двумя панелями выбор — инвентарь и список исходов. Так первый экран занимает
 * сам контракт, а не заголовок с инвентарём, и слоты не уезжают из вида, пока
 * игрок листает предметы.
 */
export default function Crafts() {
  const { user } = useAuth()
  const toast = useToast()

  const [inventory, setInventory] = useState(null)
  const [meta, setMeta] = useState(null)
  const [selected, setSelected] = useState(new Set())

  // Фильтры инвентаря.
  const [rarityFilter, setRarityFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('new') // new | rich | cheap

  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (user) {
      api.inventory().then(({ items }) => setInventory(items)).catch(() => setInventory([]))
    } else {
      setInventory([])
    }
    api.craftMeta().then(setMeta).catch(() => setMeta(null))
  }, [user])

  /** Редкости, которые контракт принимает: у топовых выхода выше нет. */
  const craftable = useMemo(
    () => new Set((meta?.craftableRarities ?? []).map((entry) => entry.id)),
    [meta],
  )
  // Пока мета не пришла, не запрещаем ничего: иначе весь инвентарь был бы
  // погашен на первых кадрах.
  const isCraftable = (rarity) => craftable.size === 0 || craftable.has(rarity)

  const byRarity = useMemo(() => {
    const groups = {}
    for (const item of inventory ?? []) {
      groups[item.rarity] = (groups[item.rarity] ?? 0) + 1
    }
    return groups
  }, [inventory])

  const selectedList = useMemo(
    () => (inventory ?? []).filter((item) => selected.has(item.id)),
    [inventory, selected],
  )

  /* Редкость набора фиксируется первым выбранным предметом — сервер не примет
     смесь. Остальные редкости после этого просто не выбираются. */
  const lockedRarity = selectedList[0]?.rarity ?? null

  const inputKopecks = selectedList.reduce((sum, item) => sum + item.priceKopecks, 0)

  // Контракт выдаёт один предмет, поэтому набор дороже потолка каталога
  // отыграть нечем — сервер такой вход отклонит. Предупреждаем заранее.
  const maxInput = meta?.maxInputKopecks ?? null
  const overLimit = maxInput !== null && inputKopecks > maxInput
  const full = selected.size === SLOT_COUNT

  /** Инвентарь после фильтров и сортировки. */
  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const found = (inventory ?? []).filter((item) => {
      if (rarityFilter !== 'all' && item.rarity !== rarityFilter) return false
      if (needle && !`${item.name} ${item.skin}`.toLowerCase().includes(needle)) return false
      return true
    })
    return found.sort((a, b) => {
      if (sort === 'rich') return b.priceKopecks - a.priceKopecks
      if (sort === 'cheap') return a.priceKopecks - b.priceKopecks
      // «Новые» — по id: он автоинкрементный, так что последнее выбитое лежит
      // сверху. Сервер отдаёт инвентарь по цене, поэтому сортируем тут.
      return b.id - a.id
    })
  }, [inventory, rarityFilter, query, sort])

  /* Шансы считаются сами, как только слоты заполнены: отдельная кнопка
     «показать шансы» была лишним шагом — набор всё равно нельзя собрать, не
     увидев исходов. Любое изменение набора гасит прошлый расчёт. */
  useEffect(() => {
    setPreview(null)
    if (selected.size !== SLOT_COUNT || overLimit) return

    let alive = true
    setPreviewLoading(true)
    api
      .craftPreview([...selected])
      .then((data) => {
        if (alive) setPreview(data)
      })
      .catch((err) => {
        if (alive) toast.error(err.message)
      })
      .finally(() => {
        if (alive) setPreviewLoading(false)
      })

    return () => {
      alive = false
    }
  }, [selected, overLimit, toast])

  function toggle(item) {
    const drop = selected.has(item.id)
    setResult(null)
    setSelected((current) => {
      const next = new Set(current)
      if (drop) next.delete(item.id)
      else if (next.size < SLOT_COUNT) next.add(item.id)
      return next
    })
    // Первый предмет задаёт редкость набора — сразу показываем только её,
    // чтобы не тыкать в заведомо недоступные карточки.
    if (!drop && selected.size === 0) setRarityFilter(item.rarity)
  }

  function reset() {
    setSelected(new Set())
    setResult(null)
    setRarityFilter('all')
  }

  async function execute() {
    if (!preview || busy) return
    setBusy(true)
    try {
      const data = await api.craftExecute([...selected])
      // Контракт тратит предметы, а не деньги — баланс трогать не нужно.
      setResult(data)
      setSelected(new Set())
      setRarityFilter('all')
      toast[data.upgraded ? 'success' : 'info'](
        `${data.upgraded ? 'В плюс! ' : 'Выпало: '}${data.drop.name} | ${data.drop.skin} — ${formatRub(data.drop.priceKopecks)}`,
      )
      api.inventory().then(({ items }) => setInventory(items)).catch(() => {})
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  /* Цвет сцены: пока набор собирается — редкость набора, после контракта —
     редкость выпавшего предмета. */
  const stageRarity = result ? result.drop.rarity : lockedRarity
  const stageStyle = {
    '--rarity': stageRarity ? RARITY_COLORS[stageRarity] : 'var(--ink-200)',
  }
  const stageState = result ? (result.upgraded ? 'is-win' : 'is-lose') : ''
  const need = SLOT_COUNT - selected.size

  return (
    <div className="page container crafts-page">
      {/* ── Сцена: слоты → выход ─────────────────────────────── */}
      <section className={`craft-stage ${stageState}`} style={stageStyle}>
        <div className="craft-row">
          <div className="craft-slots-box">
            <div className="craft-slots-head">
              <span className="craft-tag">Отдаёшь</span>
              <span className={`craft-count ${full ? 'is-full' : ''}`}>
                {selected.size}/{SLOT_COUNT}
              </span>
            </div>

            <div className="craft-slots">
              {Array.from({ length: SLOT_COUNT }).map((_, index) => {
                const item = selectedList[index]
                return item ? (
                  <button
                    key={item.id}
                    type="button"
                    className="craft-slot is-filled"
                    style={{ '--rarity': RARITY_COLORS[item.rarity] }}
                    onClick={() => toggle(item)}
                    title={`${item.name} | ${item.skin} — убрать`}
                  >
                    <WeaponArt image={item.image} rarity={item.rarity} size="sm" />
                    <span className="craft-slot-price" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <span>{formatRub(item.priceKopecks)}</span>
                      <CoinIcon size="xs" />
                    </span>
                  </button>
                ) : (
                  <span key={index} className="craft-slot">
                    {index + 1}
                  </span>
                )
              })}
            </div>

            <div className="craft-slots-foot">
              <span className="craft-foot-cap">Вложено</span>
              <span className={`craft-foot-sum ${overLimit ? 'text-red' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <span>{formatRub(inputKopecks)}</span>
                <CoinIcon size="xs" />
              </span>
            </div>
          </div>

          <div className="craft-arrow" aria-hidden="true" />

          {/* Выход: до контракта — пустой слот с «?», после — сам предмет.
              Ни матожидания, ни шанса тут нет намеренно: выход контракта —
              сюрприз, а цифры только мешали смотреть на слоты. */}
          <div className={`craft-out ${result ? 'is-done' : 'is-empty'}`}>
            <span className="craft-out-bar" aria-hidden="true" />

            {result ? (
              <>
                <span className="craft-tag">
                  {result.upgraded ? 'В плюс!' : 'Контракт закрыт'}
                </span>
                <span className="craft-out-art">
                  <span className="craft-out-glow" aria-hidden="true" />
                  <WeaponArt
                    image={result.drop.image}
                    rarity={result.drop.rarity}
                    title={result.drop.skin}
                    size="lg"
                  />
                </span>
                <span className="craft-out-weapon">{result.drop.name}</span>
                <span className="craft-out-skin">{result.drop.skin}</span>
                <span className="craft-out-foot">
                  <span className="craft-out-rarity">
                    {RARITY_LABELS[result.drop.rarity]}
                  </span>
                  <span className="craft-out-price" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>{formatRub(result.drop.priceKopecks)}</span>
                    <CoinIcon size="sm" />
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="craft-out-slot" aria-hidden="true">
                  ?
                </span>
                {/* Набор собран — подписи нет: всё, что нужно сказать, уже
                    написано на кнопке под сценой. */}
                {!preview && (
                  <span className="craft-out-hint">
                    {previewLoading
                      ? 'Проверяем набор…'
                      : `Заполни все ${SLOT_COUNT} слотов одной редкости`}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div className="craft-bar">
          {overLimit && (
            <p className="alert alert-error tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>Набор дороже, чем контракт способен отыграть: максимум —</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <span>{formatRub(maxInput)}</span>
                <CoinIcon size="xs" />
              </span>
              <span>. Убери что-нибудь подороже.</span>
            </p>
          )}

          <div className="craft-actions">
            {!user ? (
              <a
                href="/api/auth/steam"
                className="btn btn-primary btn-lg craft-go btn-steam-action"
              >
                <svg className="steam-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.005.105.005.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 12-5.373 12-12S18.605 0 11.979 0z" />
                </svg>
                <span>Войти через Steam</span>
              </a>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-lg craft-go"
                onClick={execute}
                disabled={!preview || busy || previewLoading || overLimit}
              >
                {busy
                  ? 'Собираем…'
                  : previewLoading
                    ? 'Считаем шансы…'
                    : preview
                      ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <span>Собрать за</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            <span>{formatRub(preview.inputKopecks)}</span>
                            <CoinIcon size="sm" />
                          </span>
                        </span>
                      )
                      : `Выбери ещё ${need} ${plural(need, 'предмет', 'предмета', 'предметов')}`}
              </button>
            )}

            {selected.size > 0 && (
              <button type="button" className="btn btn-ghost craft-clear" onClick={reset}>
                Сбросить
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Инвентарь ────────────────────────────────────────── */}
      <section className="card picker-panel">
        <div className="panel-head">
          <h2>Твои предметы</h2>
          {inventory && (
            <span className="tiny muted">
              {visibleItems.length} из {inventory.length}
            </span>
          )}
        </div>

        <div className="panel-tools">
          <input
            type="search"
            className="panel-search"
            placeholder="Поиск по инвентарю"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Поиск по инвентарю"
          />
          <div className="seg seg-sm">
            <button
              type="button"
              className={`seg-btn ${sort === 'new' ? 'is-active' : ''}`}
              onClick={() => setSort('new')}
            >
              Новые
            </button>
            <button
              type="button"
              className={`seg-btn ${sort === 'rich' ? 'is-active' : ''}`}
              onClick={() => setSort('rich')}
            >
              Дороже
            </button>
            <button
              type="button"
              className={`seg-btn ${sort === 'cheap' ? 'is-active' : ''}`}
              onClick={() => setSort('cheap')}
            >
              Дешевле
            </button>
          </div>

          <div className="rarity-filter" role="group" aria-label="Редкость">
            <button
              type="button"
              className={`rarity-chip ${rarityFilter === 'all' ? 'is-active' : ''}`}
              onClick={() => setRarityFilter('all')}
            >
              Все
            </button>
            {Object.entries(byRarity)
              .sort((a, b) => b[1] - a[1])
              .map(([rarity, count]) => {
                // Гасим то, что в набор уже не влезет: чужую редкость при
                // начатом контракте и топ, который контракт не принимает.
                const dim =
                  !isCraftable(rarity) || (lockedRarity && rarity !== lockedRarity)
                return (
                  <button
                    key={rarity}
                    type="button"
                    className={`rarity-chip ${rarityFilter === rarity ? 'is-active' : ''} ${
                      dim ? 'is-dim' : ''
                    }`}
                    onClick={() => setRarityFilter(rarity)}
                    style={{ '--chip': RARITY_COLORS[rarity] }}
                    title={RARITY_LABELS[rarity]}
                  >
                    {count}
                  </button>
                )
              })}
          </div>
        </div>

        {!user ? (
          <div className="panel-empty">
            <p className="muted">Войди через Steam, чтобы использовать предметы из инвентаря для крафта.</p>
            <a href="/api/auth/steam" className="btn btn-steam btn-sm">
              <svg className="steam-icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.005.105.005.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 12-5.373 12-12S18.605 0 11.979 0z" />
              </svg>
              <span>Войти через Steam</span>
            </a>
          </div>
        ) : inventory === null ? (
          <div className="skeleton" style={{ height: 260, margin: 18 }} />
        ) : inventory.length === 0 ? (
          <div className="panel-empty">
            <p className="muted">Инвентарь пуст — собирать контракт пока не из чего.</p>
            <Link to="/cases" className="btn btn-soft btn-sm">
              Открыть кейс
            </Link>
          </div>
        ) : visibleItems.length === 0 ? (
          <p className="panel-empty muted">По этим фильтрам ничего не нашлось.</p>
        ) : (
          <div className="picker-grid">
            {visibleItems.map((item) => {
              const picked = selected.has(item.id)
              return (
                <ItemCard
                  key={item.id}
                  item={item}
                  size="sm"
                  selected={picked}
                  onClick={() => toggle(item)}
                  disabled={
                    !picked &&
                    (full ||
                      !isCraftable(item.rarity) ||
                      (lockedRarity !== null && item.rarity !== lockedRarity))
                  }
                />
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
